import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import { api, formatApiError } from "@/lib/api";
import { ORDER_STATUS_FLOW, ORDER_STATUS_LABELS, orderStatusTone } from "@/lib/constants";
import { toInputDate, fromInputDate, formatCurrency } from "@/lib/format";
import { useAuth, hasRole } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/useIsMobile";

const emptyOrder = () => ({
  job_id: "",
  supplier_id: "",
  product_category_id: "",
  description: "",
  supplier_reference: "",
  order_date: "",
  order_amount: "0",
  estimated_delivery_date: "",
  actual_delivery_date: "",
  status: "taslak",
  responsible_user_id: "",
  delay_reason: "",
  notes: "",
});

export default function OrderFormPage() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const isNew = !id || id === "yeni";
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const isAdmin = hasRole(user, "admin");
  const canEdit = !isMobile && hasRole(user, "admin", "sales", "order_manager");

  // Direct mobile access to /siparisler/yeni should redirect to /siparisler
  useEffect(() => {
    if (isNew && isMobile) {
      navigate("/siparisler", { replace: true });
    }
  }, [isNew, isMobile, navigate]);

  const [form, setForm] = useState(emptyOrder());
  const [order, setOrder] = useState(null);
  const [job, setJob] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [orderMgrs, setOrderMgrs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [jobSearch, setJobSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);

  const selectJobs = useMemo(() => {
    const list = [...jobs];
    if (job && !list.some((j) => j.id === job.id)) {
      list.push({ id: job.id, job_number: job.job_number, customer_name: job.customer_name });
    }
    return list;
  }, [jobs, job]);

  useEffect(() => {
    (async () => {
      const [s, p, om] = await Promise.all([
        api.get("/suppliers"),
        api.get("/product-categories"),
        api.get("/users/order-managers"),
      ]);
      setSuppliers(s.data);
      setProductCategories(p.data);
      setOrderMgrs(om.data);
    })();
  }, []);

  // Job lookup — search-based (max 25 results)
  useEffect(() => {
    const params = jobSearch ? { q: jobSearch, limit: 25 } : { limit: 25 };
    api.get("/jobs/lookup", { params }).then((r) => setJobs(r.data)).catch(() => {});
  }, [jobSearch]);

  useEffect(() => {
    if (isNew) {
      const jid = sp.get("job_id");
      if (jid) setForm((f) => ({ ...f, job_id: jid }));
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/orders/${id}`);
        setOrder(r.data);
        setForm({
          job_id: r.data.job_id,
          supplier_id: r.data.supplier_id || "",
          product_category_id: r.data.product_category_id || "",
          description: r.data.description || "",
          supplier_reference: r.data.supplier_reference || "",
          order_date: toInputDate(r.data.order_date),
          order_amount: String(r.data.order_amount || "0"),
          estimated_delivery_date: toInputDate(r.data.estimated_delivery_date),
          actual_delivery_date: toInputDate(r.data.actual_delivery_date),
          status: r.data.status,
          responsible_user_id: r.data.responsible_user_id || "",
          delay_reason: r.data.delay_reason || "",
          notes: r.data.notes || "",
        });
      } catch (err) {
        toast.error(formatApiError(err));
        navigate("/siparisler");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    if (!form.job_id) { setJob(null); return; }
    (async () => {
      try {
        const r = await api.get(`/jobs/${form.job_id}`);
        setJob(r.data);
      } catch (_) { setJob(null); }
    })();
  }, [form.job_id]);

  const handleDeleteOrder = async () => {
    if (isMobile) return;
    if (!window.confirm("Bu sipariş kaydını KALICI OLARAK silmek istediğinize emin misiniz? Bu işlem geri alınamaz!")) return;
    const confirmText = window.prompt("Lütfen silme işlemini onaylamak için büyük harflerle 'SİL' yazın:");
    if (confirmText !== "SİL") {
      toast.error("Silme işlemi onaylanmadı.");
      return;
    }
    try {
      await api.delete(`/orders/${order.id}`);
      toast.success("Sipariş kalıcı olarak silindi.");
      navigate("/siparisler");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submit = async () => {
    if (isMobile) return;
    if (!form.job_id) { toast.error("Bir iş seçilmelidir."); return; }
    if (parseFloat(form.order_amount) < 0) { toast.error("Sipariş tutarı negatif olamaz."); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        supplier_id: form.supplier_id || null,
        product_category_id: form.product_category_id || null,
        responsible_user_id: form.responsible_user_id || null,
        order_date: fromInputDate(form.order_date),
        estimated_delivery_date: fromInputDate(form.estimated_delivery_date),
        actual_delivery_date: fromInputDate(form.actual_delivery_date),
        order_amount: form.order_amount || "0",
      };
      if (isNew) {
        const r = await api.post("/orders", payload);
        toast.success(`Sipariş oluşturuldu: ${r.data.order_number}`);
        navigate(`/siparisler/${r.data.id}`);
      } else {
        payload.version = order.version;
        const r = await api.patch(`/orders/${order.id}`, payload);
        toast.success("Sipariş güncellendi.");
        setOrder(r.data);
      }
    } catch (err) {
      toast.error(formatApiError(err, "Kaydetme başarısız."));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center gap-2 py-16 text-slate-500" data-testid="order-loading"><Loader2 className="animate-spin text-[#FF353A]" size={16} /> Yükleniyor...</div>;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 pb-[calc(5rem+env(safe-area-inset-bottom,0px))] md:pb-8 space-y-6 max-w-5xl bg-[#F4F6F8] min-h-full" data-testid="order-form-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate("/siparisler")} className="border-[#DCE2E8]"><ArrowLeft size={16} className="mr-1" /> Listeye Dön</Button>
          <div>
            <h1 className="text-2xl font-bold text-[#171A1F] tracking-tight">{isNew ? "Yeni Sipariş" : order?.order_number}</h1>
            {!isNew && (
              <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                <span>Sürüm v{order?.version}</span>
                <StatusBadge tone={orderStatusTone(order?.status)}>{ORDER_STATUS_LABELS[order?.status]}</StatusBadge>
              </div>
            )}
          </div>
        </div>
        {!isMobile && (
          <div className="flex items-center gap-2">
            {isAdmin && !isNew && (
              <Button
                variant="destructive"
                onClick={handleDeleteOrder}
                data-testid="order-delete-button"
              >
                Kalıcı Olarak Sil
              </Button>
            )}
            {canEdit && (
              <Button onClick={submit} disabled={saving} className="bg-[#FF353A] hover:bg-[#E02B30] text-white shadow-sm" data-testid="order-save-button">
                {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Kaydediliyor</> : <><Save size={16} className="mr-2" /> Kaydet</>}
              </Button>
            )}
          </div>
        )}
      </div>

      {job && (
        <div className="rounded-lg border border-[#DCE2E8] bg-white p-4 grid gap-3 md:grid-cols-4 shadow-sm">
          <Info label="İş No" value={job.job_number} mono />
          <Info label="Müşteri" value={job.customer_name} />
          <Info label="İlçe / Şehir" value={[job.district, job.city].filter(Boolean).join(" / ")} />
          <Info label="Satış Tutarı" value={formatCurrency(job.sales_amount)} mono />
        </div>
      )}

      <div className="rounded-lg border border-[#DCE2E8] bg-white p-6 grid gap-4 md:grid-cols-3 shadow-sm">
        <Field label="İş *">
          <Select value={form.job_id || "__none__"} onValueChange={(v) => setForm((f) => ({ ...f, job_id: v === "__none__" ? "" : v }))} disabled={!canEdit || !isNew}>
            <SelectTrigger className="border-[#DCE2E8]" data-testid="order-job-select"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
            <SelectContent className="bg-white border-[#DCE2E8] max-h-80">
              <div className="p-2 sticky top-0 bg-white z-10 border-b border-[#DCE2E8]">
                <Input
                  placeholder="İş no, müşteri ara..."
                  value={jobSearch}
                  onChange={(e) => setJobSearch(e.target.value)}
                  className="h-8 text-sm border-[#DCE2E8]"
                  data-testid="order-job-search"
                />
              </div>
              <SelectItem value="__none__">— Seçiniz —</SelectItem>
              {selectJobs.map((j) => (<SelectItem key={j.id} value={j.id}>{j.job_number} · {j.customer_name}</SelectItem>))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Tedarikçi">
          <Select value={form.supplier_id || "__none__"} onValueChange={(v) => setForm((f) => ({ ...f, supplier_id: v === "__none__" ? "" : v }))} disabled={!canEdit}>
            <SelectTrigger className="border-[#DCE2E8]" data-testid="order-supplier-select"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
            <SelectContent className="bg-white border-[#DCE2E8]">
              <SelectItem value="__none__">— Seçilmedi —</SelectItem>
              {suppliers.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Ürün Grubu">
          <Select value={form.product_category_id || "__none__"} onValueChange={(v) => setForm((f) => ({ ...f, product_category_id: v === "__none__" ? "" : v }))} disabled={!canEdit}>
            <SelectTrigger className="border-[#DCE2E8]"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
            <SelectContent className="bg-white border-[#DCE2E8]">
              <SelectItem value="__none__">— Seçilmedi —</SelectItem>
              {productCategories.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Tedarikçi Referansı"><Input value={form.supplier_reference} onChange={(e) => setForm((f) => ({ ...f, supplier_reference: e.target.value }))} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" /></Field>
        <Field label="Sipariş Tarihi"><Input type="date" value={form.order_date} onChange={(e) => setForm((f) => ({ ...f, order_date: e.target.value }))} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" /></Field>
        <Field label="Sipariş Tutarı (KDV Dahil)"><Input inputMode="decimal" value={form.order_amount} onChange={(e) => setForm((f) => ({ ...f, order_amount: e.target.value.replace(",", ".") }))} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8] font-mono" data-testid="order-amount" /></Field>
        <Field label="Tahmini Teslim"><Input type="date" value={form.estimated_delivery_date} onChange={(e) => setForm((f) => ({ ...f, estimated_delivery_date: e.target.value }))} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" /></Field>
        <Field label="Gerçek Teslim"><Input type="date" value={form.actual_delivery_date} onChange={(e) => setForm((f) => ({ ...f, actual_delivery_date: e.target.value }))} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" /></Field>
        <Field label="Durum">
          <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))} disabled={!canEdit}>
            <SelectTrigger className="border-[#DCE2E8]" data-testid="order-status-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-[#DCE2E8]">
              {ORDER_STATUS_FLOW.map(([k, l]) => (<SelectItem key={k} value={k}>{l}</SelectItem>))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Sorumlu Kullanıcı">
          <Select value={form.responsible_user_id || "__none__"} onValueChange={(v) => setForm((f) => ({ ...f, responsible_user_id: v === "__none__" ? "" : v }))} disabled={!canEdit}>
            <SelectTrigger className="border-[#DCE2E8]"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
            <SelectContent className="bg-white border-[#DCE2E8]">
              <SelectItem value="__none__">— Seçilmedi —</SelectItem>
              {orderMgrs.map((u) => (<SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Açıklama" full><Textarea rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" /></Field>
        <Field label="Gecikme Nedeni" full><Textarea rows={2} value={form.delay_reason} onChange={(e) => setForm((f) => ({ ...f, delay_reason: e.target.value }))} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" /></Field>
        <Field label="Notlar" full><Textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} disabled={!canEdit} readOnly={isMobile} className="border-[#DCE2E8]" /></Field>
      </div>

      {/* Mobile Sticky Action Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-[#DCE2E8] p-3 z-30 flex items-center justify-between gap-3 shadow-lg pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))]">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => navigate("/siparisler")}
          className="border-[#DCE2E8] min-h-[44px] w-full"
          data-testid="order-mobile-back-button"
        >
          <ArrowLeft size={16} className="mr-1" /> Geri
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={`space-y-1.5 ${full ? "md:col-span-3" : ""}`}>
      <Label className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{label}</Label>
      {children}
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-sm text-[#171A1F] ${mono ? "font-mono font-semibold text-[#FF353A]" : "font-medium"}`}>{value || "-"}</div>
    </div>
  );
}
